// components/use-case-details/use-case-workflow/workflow-component-registry.tsx
import React from 'react';

import WorkflowVisualization from './use-case-visualization-component';
import AgentCard from './use-case-workflow-agent-card-component';
import ChatShowcase from './use-case-workflow-example-card';
import HITLComponent from './use-case-workflow-hitl-component';
import UseCaseKeyBenefitsCard from './use-case-workflow-key-benefits-card';
import ProcessOverviewCard from './use-case-workflow-process-overview-card';
import { InsightCard } from './use-case-workflow-section';
import VectorDBComponent from './use-case-workflow-vector-db-component';

// Simple component to display data source information
const DataSourceCard = ({ 
  title, 
  description, 
  source_type, 
  source_name, 
  data_format,
  connection_details,
  borderColor = "bg-blue-600"
}: { 
  title: string;
  description: string;
  source_type?: string;
  source_name?: string;
  data_format?: string;
  connection_details?: any;
  borderColor?: string;
}) => {
  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden bg-gray-800/90">
      <div className="relative p-4 border-b border-gray-700">
        <div className={`absolute top-0 left-0 w-full h-1 ${borderColor}`} />
        <h3 className="text-xl font-bold">{title}</h3>
        <div className="flex items-center mt-1 gap-2">
          <span className="px-2 py-0.5 bg-gray-700 text-white text-xs rounded">
            {source_type || 'Data Source'}
          </span>
          {data_format && (
            <span className="px-2 py-0.5 bg-gray-700 text-white text-xs rounded">
              {data_format}
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <p className="text-gray-300 mb-4">{description}</p>
        
        {source_name && (
          <div className="mb-3">
            <span className="text-xs text-gray-400">Source:</span>
            <div className="text-sm text-white mt-1">{source_name}</div>
          </div>
        )}
        
        {connection_details && Object.keys(connection_details).length > 0 && (
          <div>
            <span className="text-xs text-gray-400">Connection Details:</span>
            <div className="bg-gray-900 p-2 rounded mt-1 text-xs text-gray-300 overflow-x-auto">
              <pre>{JSON.stringify(connection_details, null, 2)}</pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Simple component to display tool information
const ToolCard = ({ 
  title, 
  description, 
  tool_type,
  tool_name,
  website_url,
  input_data_format,
  output_data_format,
  parameters,
  borderColor = "bg-indigo-600"
}: { 
  title: string;
  description: string;
  tool_type?: string;
  tool_name?: string;
  website_url?: string;
  input_data_format?: string;
  output_data_format?: string;
  parameters?: Record<string, any>;
  borderColor?: string;
}) => {
  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden bg-gray-800/90">
      <div className="relative p-4 border-b border-gray-700">
        <div className={`absolute top-0 left-0 w-full h-1 ${borderColor}`} />
        <h3 className="text-xl font-bold">{title}</h3>
        <div className="flex items-center mt-1 gap-2">
          <span className="px-2 py-0.5 bg-gray-700 text-white text-xs rounded">
            {tool_type || 'Tool'}
          </span>
          {input_data_format && output_data_format && (
            <span className="px-2 py-0.5 bg-gray-700 text-white text-xs rounded">
              {input_data_format} → {output_data_format}
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <p className="text-gray-300 mb-4">{description}</p>
        
        {tool_name && (
          <div className="mb-3">
            <span className="text-xs text-gray-400">Tool:</span>
            <div className="text-sm text-white mt-1">{tool_name}</div>
          </div>
        )}
        
        {website_url && (
          <div className="mb-3">
            <span className="text-xs text-gray-400">Website:</span>
            <div className="mt-1">
              <a 
                href={website_url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-blue-400 hover:underline text-sm"
              >
                {website_url}
              </a>
            </div>
          </div>
        )}
        
        {parameters && Object.keys(parameters).length > 0 && (
          <div>
            <span className="text-xs text-gray-400">Parameters:</span>
            <div className="bg-gray-900 p-2 rounded mt-1 text-xs text-gray-300 overflow-x-auto">
              <pre>{JSON.stringify(parameters, null, 2)}</pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const componentRegistry: Record<string, React.ComponentType<any>> = {
  // Main component types
  'agentCard': AgentCard,
  'processOverviewCard': ProcessOverviewCard,
  'chatShowcase': ChatShowcase,
  'keyBenefitsCard': UseCaseKeyBenefitsCard,
  'vectorDBComponent': VectorDBComponent,
  'insightCard': InsightCard,
  'workflowVisualization': WorkflowVisualization,
  'hitlComponent': HITLComponent,
  'dataSourceCard': DataSourceCard,
  'toolCard': ToolCard,
  
  // Legacy aliases for backward compatibility
  'vectorDbComponent': VectorDBComponent,
  
  // Default component for unknown types
  'default': ({ type, ...props }: { type: string }) => (
    <div className="p-4 bg-red-900/20 rounded-lg border border-red-800">
      <p className="text-red-300">Unknown component type: {type}</p>
      <pre className="text-xs text-red-300 mt-2 overflow-auto">
        {JSON.stringify(props, null, 2)}
      </pre>
    </div>
  )
};

export function getWorkflowComponentByType(type: string): React.ComponentType<any> {
  return componentRegistry[type] || componentRegistry.default;
} 